import { memo, useCallback } from 'react';
import { css } from '@emotion/react';
import { Icon } from '@frontend/icons';
import { useNavSize } from '@frontend/responsiveness';
import { useScopedAppFlagStore } from '@frontend/scope';
import { NotificationBadge, NakedButton } from '@frontend/design-system';
import { useNotificationsState } from '../../hooks/use-notifications-state';
import { iconButtonStyles } from '../../styles/styles';
import { focusTopBarTarget, TopBarFocusIds } from '../../utils/focus-registry';

type NotificationTrigger = {
  count: number;
  showFull: boolean;
  isSelected: boolean;
  isMobile: boolean;
  onClick: () => void;
  handleHover: (isHovering: boolean) => void;
};

export const NotificationTrigger = memo(
  ({ count, showFull, isSelected, isMobile, onClick, handleHover }: NotificationTrigger) => (
    <span onMouseEnter={() => handleHover(true)} onMouseLeave={() => handleHover(false)}>
      {count ? (
        <NotificationBadge
          css={css`
            position: absolute;
            right: 8px;
            top: 12px;
            z-index: 0;
          `}
        >
          {count > 99 ? (showFull ? count : '99+') : count}
        </NotificationBadge>
      ) : null}
      <NakedButton
        css={[iconButtonStyles({ isSelected, isMobile })]}
        onClick={onClick}
        id={TopBarFocusIds.notifications}
        data-testid='notification-tray-trigger'
        trackingId='notify-2.0-tray-trigger'
      >
        <Icon name='notification' />
      </NakedButton>
    </span>
  )
);
NotificationTrigger.displayName = 'NotificationTrigger';

export const NotificationButton = () => {
  const navSize = useNavSize();
  const isMobile = navSize.isLte('mobile');
  const { getFeatureFlagValue } = useScopedAppFlagStore();
  const showForYouNotificationsTab = getFeatureFlagValue('for-you-notifications');
  const showGroupedForYouNotifications = getFeatureFlagValue('grouping-for-you-notifications');

  const notificationsState = useNotificationsState({
    showForYouNotificationsTab,
    showGroupedForYouNotifications,
    isMobile,
  });
  const {
    isNotificationsOpen,
    setIsNotificationsOpen,
    count: newNotificationCountExact,
    showFullCount: showFullNotificationCount,
    setIsHovering,
  } = notificationsState;

  const handleHover = useCallback(
    (isHovering: boolean) => {
      setIsHovering(isHovering);
    },
    [setIsHovering]
  );

  return (
    <NotificationTrigger
      handleHover={handleHover}
      count={newNotificationCountExact}
      showFull={showFullNotificationCount}
      isSelected={isNotificationsOpen}
      isMobile={isMobile}
      onClick={() => {
        if (isNotificationsOpen) {
          focusTopBarTarget('notifications');
        }

        setIsNotificationsOpen(!isNotificationsOpen);
      }}
    />
  );
};

export const NotificationButtonWrapper = () => {
  const { getFeatureFlagValue } = useScopedAppFlagStore();
  const hideNotificationBell = getFeatureFlagValue('hideNotificationBell');

  return (
    !hideNotificationBell && (
      <li css={{ position: 'relative' }}>
        <NotificationButton />
      </li>
    )
  );
};
